import React, { useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import TimeFilter, { TimeFilterOption } from './TimeFilter';
import { PortfolioVsMarketProduct } from '../services/investmentService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface PortfolioVsMarketChartProps {
  data: PortfolioVsMarketProduct[];
}

const PortfolioVsMarketChart: React.FC<PortfolioVsMarketChartProps> = ({ data }) => {
  const [selectedFilter, setSelectedFilter] = useState<TimeFilterOption>('MAX');
  const [selectedProductId, setSelectedProductId] = useState<number | null>(data[0]?.product_id ?? null);

  const selectedProduct = useMemo(() => {
    if (!selectedProductId) {
      return data[0] ?? null;
    }
    return data.find((product) => product.product_id === selectedProductId) ?? data[0] ?? null;
  }, [data, selectedProductId]);

  const filteredEvolution = useMemo(() => {
    if (!selectedProduct) {
      return [];
    }

    if (selectedFilter === 'MAX') {
      return selectedProduct.evolution.slice(-10);
    }

    const now = new Date();
    let daysBack = 0;

    switch (selectedFilter) {
      case '30D':
        daysBack = 30;
        break;
      case '60D':
        daysBack = 60;
        break;
      case '90D':
        daysBack = 90;
        break;
      case '180D':
        daysBack = 180;
        break;
      case '1Y':
        daysBack = 365;
        break;
      case '2Y':
        daysBack = 730;
        break;
      case '5Y':
        daysBack = 1825;
        break;
      default:
        daysBack = 0;
        break;
    }

    const cutoffDate = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000);
    return selectedProduct.evolution.filter((point) => new Date(point.date) >= cutoffDate);
  }, [selectedProduct, selectedFilter]);

  const chartData = useMemo(() => {
    const labels = filteredEvolution.map((point) => new Date(point.date).toLocaleDateString('pt-PT'));
    return {
      labels,
      datasets: [
        {
          label: 'Portefólio',
          data: filteredEvolution.map((point) => point.portfolio_variation_percent),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          borderWidth: 2,
          fill: false,
          tension: 0.35,
          pointBackgroundColor: 'rgb(59, 130, 246)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          spanGaps: true,
        },
        {
          label: `Mercado${selectedProduct ? ` (${selectedProduct.market_code})` : ''}`,
          data: filteredEvolution.map((point) => point.market_variation_percent),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          borderWidth: 2,
          fill: false,
          tension: 0.35,
          pointBackgroundColor: 'rgb(16, 185, 129)',
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          spanGaps: true,
        },
      ],
    };
  }, [filteredEvolution, selectedProduct]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
        }
      },
      title: {
        display: false,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: function(context: any) {
            const value = context.parsed.y;
            if (value === null || value === undefined) return `${context.dataset.label}: sem dados`;
            return `${context.dataset.label}: ${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Data'
        },
        grid: {
          display: false
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Variação (%)'
        },
        beginAtZero: true,
        ticks: {
          callback: function(value: any) {
            return value >= 0 ? '+' + value.toFixed(1) + '%' : value.toFixed(1) + '%';
          }
        }
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false
    }
  };

  if (!selectedProduct) {
    return <div className="text-sm text-gray-500">Sem produtos com código de mercado configurado.</div>;
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2 justify-center">
        {data.map((product) => (
          <button
            key={product.product_id}
            onClick={() => setSelectedProductId(product.product_id)}
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              selectedProduct.product_id === product.product_id
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {product.product_name}
          </button>
        ))}
      </div>

      <div className="mb-4">
        <TimeFilter
          selectedFilter={selectedFilter}
          onFilterChange={setSelectedFilter}
          className="justify-center"
        />
      </div>

      <div className="h-80">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default PortfolioVsMarketChart;
